import type { MonthCell, Task } from "@app/core";
import { cn } from "@app/ui";
import { memo, useMemo, type DragEvent, type MouseEvent } from "react";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { suppressMobileContextMenu } from "../../lib/mobile-context-menu.js";
import { useI18n } from "../../lib/useI18n.js";
import { AllDayChip } from "./AllDayChip.js";
import { EventChip } from "./EventChip.js";
import { getAllDayPosition, isMultiDay } from "./month-helpers.js";

export interface MonthDayCellProps {
  cell: MonthCell;
  isToday: boolean;
  /** Cell is inside the live drag/create/resize highlight range. */
  isHighlighted: boolean;
  /** Cell is the current HTML5-DnD drop target. */
  isDropTarget: boolean;
  /** Cell's hidden overflow tasks are expanded (via "+N more"). */
  isExpanded: boolean;
  /** Per-week all-day row assignment (task id → row). */
  rowMap: Map<string, number>;
  weekMaxRow: number;
  /** How many chip rows (all-day + timed) fit in the cell at its current height. */
  rowCapacity: number;
  onCellEnter: (date: Date) => void;
  onDragOverCell: (iso: string) => void;
  onDragLeaveCell: (iso: string) => void;
  onDropCell: (e: DragEvent, date: Date) => void;
  onContextMenuCell: (e: MouseEvent, date: Date) => void;
  onMouseDownCell: (e: MouseEvent, date: Date) => void;
  onEditTask: (t: Task) => void;
  onMoveStart: (e: MouseEvent, t: Task, grabDate: Date) => void;
  onResizeLeft: (e: MouseEvent, t: Task) => void;
  onResizeRight: (e: MouseEvent, t: Task) => void;
  onSetExpanded: (iso: string | null) => void;
}

function MonthDayCellImpl({
  cell: c, isToday, isHighlighted, isDropTarget, isExpanded, rowMap, weekMaxRow, rowCapacity,
  onCellEnter, onDragOverCell, onDragLeaveCell, onDropCell, onContextMenuCell,
  onMouseDownCell, onEditTask, onMoveStart, onResizeLeft, onResizeRight, onSetExpanded,
}: MonthDayCellProps) {
  const mobile = useMobileUx();
  const { t } = useI18n();
  const iso = c.date.toISOString();
  const allDayTasks = useMemo(() => c.tasks.filter((t) => t.all_day), [c.tasks]);
  const tasksByRow = new Map(allDayTasks.map((t) => [rowMap.get(t.id) ?? 0, t]));
  const timedTasks = useMemo(
    () => c.tasks.filter((t) => !t.all_day).sort((a, b) => {
      // Events take priority over tasks for the top of the cell.
      const ev = Number(b.is_event) - Number(a.is_event);
      if (ev !== 0) return ev;
      return (a.due_at ?? "").localeCompare(b.due_at ?? "");
    }),
    [c.tasks],
  );
  // All-day tasks own their rows; any EMPTY all-day row in THIS cell is filled
  // with a leftover timed event (prioritise all-day, but don't waste the slot).
  // Remaining timed events flow below, up to the leftover capacity.
  const timedQueue = [...timedTasks];
  const gapFill = new Map<number, Task>();
  for (let row = 0; row <= weekMaxRow; row++) {
    if (!tasksByRow.has(row)) {
      const t = timedQueue.shift();
      if (t) gapFill.set(row, t);
    }
  }
  const cap = Math.max(0, rowCapacity - (weekMaxRow + 1));
  // Reserve one row for the "+N more" toggle when overflowing, so it always fits
  // inside the cell instead of being clipped off the bottom.
  const overflowing = !isExpanded && timedQueue.length > cap;
  const visibleTimed = isExpanded ? timedQueue : timedQueue.slice(0, overflowing ? Math.max(0, cap - 1) : cap);
  const overflowCount = timedQueue.length - visibleTimed.length;

  return (
    <div
      data-day-iso={iso}
      onMouseEnter={() => onCellEnter(c.date)}
      onDragOver={(e) => { e.preventDefault(); onDragOverCell(iso); }}
      onDragLeave={() => onDragLeaveCell(iso)}
      onDrop={(e) => onDropCell(e, c.date)}
      onContextMenu={(e) => {
        if (suppressMobileContextMenu(e)) return;
        if ((e.target as HTMLElement).closest("[data-chip]")) return;
        e.preventDefault();
        onContextMenuCell(e, c.date);
      }}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest("[data-chip]")) return;
        onMouseDownCell(e, c.date);
      }}
      className={cn(
        "group cursor-pointer overflow-hidden bg-card transition-colors",
        mobile ? "p-1" : "p-1.5",
        !c.inMonth && "bg-muted/40 text-muted-foreground",
        isToday && "bg-primary/5 ring-1 ring-inset ring-primary/40",
        isDropTarget && "bg-accent",
        isHighlighted && "bg-primary/10",
        // Expanded: let the full list pop over neighbours (shadow floats it).
        isExpanded && "relative z-10 overflow-visible shadow-lg ring-1 ring-ring/40",
      )}
    >
      <div className={cn(
        "inline-flex items-center justify-center rounded-full px-1",
        mobile ? "h-4 min-w-4 text-[calc(10px*var(--app-text-scale))]" : "h-5 min-w-5 text-xs",
        isToday && "bg-primary text-primary-foreground font-semibold",
      )}>
        {c.date.getDate()}
      </div>
      <div data-cell-rows className={cn("flex flex-col", mobile ? "mt-0.5 gap-px" : "mt-1 gap-0.5")}>
        {Array.from({ length: weekMaxRow + 1 }, (_, row) => {
          const t = tasksByRow.get(row);
          if (!t) {
            // Empty all-day slot — fill with a leftover timed event if one's queued.
            const filler = gapFill.get(row);
            return filler
              ? <EventChip key={filler.id} task={filler} withTime={true} compact={mobile} onClick={(x) => onEditTask(x)} />
              : <div key={row} className={mobile ? "h-4" : "h-5"} />;
          }
          const position = getAllDayPosition(t, c.date);
          return (
            <AllDayChip
              key={`${t.id}-${iso}`}
              task={t}
              isFirst={position === "first" || position === "only"}
              isLast={position === "last" || position === "only"}
              date={c.date}
              overlapPx={7}
              compact={mobile}
              canResizeLeft={isMultiDay(t)}
              canResizeRight={isMultiDay(t)}
              onClick={() => onEditTask(t)}
              onMoveStart={(e) => onMoveStart(e, t, c.date)}
              onResizeLeft={(e) => onResizeLeft(e, t)}
              onResizeRight={(e) => onResizeRight(e, t)}
            />
          );
        })}
        {visibleTimed.map((t) => (
          <EventChip key={t.id} task={t} withTime={true} compact={mobile} onClick={(x) => onEditTask(x)} />
        ))}
        {overflowCount > 0 && (
          <button
            type="button"
            className={cn("rounded px-1 text-left text-muted-foreground hover:bg-accent hover:text-foreground", mobile ? "text-[calc(9px*var(--app-text-scale))]" : "text-[calc(10px*var(--app-text-scale))]")}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onSetExpanded(iso); }}
          >
            +{overflowCount} more
          </button>
        )}
        {isExpanded && overflowCount === 0 && timedTasks.length > cap && (
          <button
            type="button"
            className={cn("rounded px-1 text-left text-muted-foreground hover:bg-accent hover:text-foreground", mobile ? "text-[calc(9px*var(--app-text-scale))]" : "text-[calc(10px*var(--app-text-scale))]")}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onSetExpanded(null); }}
          >
            {t("calendar.showLess")}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Re-render only when a cell's DATA changes — same contract as `DayColumn`.
 * A drag-hover / drop-target / "+N more" toggle repaints only the cell(s) whose
 * boolean actually flipped, and a data edit repaints only the day(s) it touched
 * (`useMonthCells` preserves untouched cells' object identity). Function props
 * are intentionally ignored, so a cell may keep STALE closures across an edit.
 * That is only safe because every handler reached from here reads its task data
 * LIVE from the store at call time (`useMonthDrag` uses `getState()`) — never
 * reintroduce a closed-over `tasks` snapshot into one of these handlers.
 */
export const MonthDayCell = memo(MonthDayCellImpl, (a, b) =>
  a.cell === b.cell &&
  a.isToday === b.isToday &&
  a.isHighlighted === b.isHighlighted &&
  a.isDropTarget === b.isDropTarget &&
  a.isExpanded === b.isExpanded &&
  a.rowMap === b.rowMap &&
  a.weekMaxRow === b.weekMaxRow &&
  a.rowCapacity === b.rowCapacity,
);
